import { AlertCircle, ArrowLeft } from 'lucide-react'
import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { DASH } from './detail-page-utils'
import { LoadingSpinner } from './LoadingSpinner'

export function DescriptionField({
  label,
  value,
  preserveLineBreaks = false,
}: {
  label: string
  value: ReactNode
  preserveLineBreaks?: boolean
}) {
  const display = value === '' || value == null ? DASH : value
  const dashed = display === DASH
  const lineBreaks = preserveLineBreaks ? ' whitespace-pre-line' : ''
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className={`mt-1 text-sm${lineBreaks} ${dashed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}
      >
        {display}
      </dd>
    </div>
  )
}

const GRID_COLS_CLASS: Record<1 | 2 | 3, string> = {
  1: '',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
}

export function DescriptionList({ children, cols = 2 }: { children: ReactNode; cols?: 1 | 2 | 3 }) {
  // eslint-disable-next-line security/detect-object-injection
  const colsClass = GRID_COLS_CLASS[cols]
  return <dl className={`grid grid-cols-1 ${colsClass} gap-x-6 gap-y-4`}>{children}</dl>
}

export function DetailPageLoading({ entityLabel }: { entityLabel: string }) {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-3">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading {entityLabel}...</p>
      </div>
    </div>
  )
}

export function DetailPageError({
  entityLabel,
  backTo,
  backLabel,
  error,
}: {
  entityLabel: string
  backTo: string
  backLabel: string
  error: Error | null
}) {
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Link
        to={backTo}
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        {backLabel}
      </Link>
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
              Error loading {entityLabel}
            </h3>
            <p className="mt-1 text-sm text-red-700 dark:text-red-300">
              {error
                ? `Failed to load ${entityLabel}. Please try again.`
                : `${entityLabel.charAt(0).toUpperCase()}${entityLabel.slice(1)} not found.`}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
